import React, { useEffect, useState, useCallback } from "react";
import { api, brl, formatDateTime } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import {
  Gauge, TrendingUp, TrendingDown, Minus, ArrowRight, CheckCircle2, XCircle,
  ShieldAlert, ShieldCheck, AlertTriangle, RefreshCw, History, Sparkles, X, Bot,
} from "lucide-react";

const PURPLE = "#5B2CA0";
const DEEP = "#2B1E4A";

// Mapa de risco -> cor/rotulo/icone (nunca so cor: sempre com texto+icone por acessibilidade)
const RISK = {
  muito_baixo: { c: "#2E7D46", bg: "#EAF6EE", label: "Muito baixo", Icon: ShieldCheck },
  baixo: { c: "#3F6B45", bg: "#EFF5EF", label: "Baixo", Icon: ShieldCheck },
  medio: { c: "#B7891B", bg: "#FBF6E7", label: "Moderado", Icon: AlertTriangle },
  elevado: { c: "#C2560A", bg: "#FCEEE2", label: "Elevado", Icon: ShieldAlert },
  alto: { c: "#B4564F", bg: "#FBEDED", label: "Alto", Icon: ShieldAlert },
};
const CATEGORY = {
  INCREASE: { label: "Oportunidade de aumento", c: "#2E7D46", bg: "#EAF6EE" },
  MAINTAIN: { label: "Manter limite", c: "#5C5A57", bg: "#F2EFEA" },
  REVIEW: { label: "Revisão recomendada", c: "#C2560A", bg: "#FCEEE2" },
  REDUCE: { label: "Possível redução", c: "#B4564F", bg: "#FBEDED" },
};
const BLOCKED = {
  parcela_vencida: "Possui parcela vencida — aumento automático bloqueado",
  atraso_grave_30d: "Atraso superior a 30 dias no histórico",
  intervalo_minimo: "Aumento recente — aguardando intervalo mínimo entre aumentos",
};

function RiskBadge({ risk, testid }) {
  const r = RISK[risk] || RISK.medio;
  const I = r.Icon;
  return (
    <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold"
      style={{ color: r.c, background: r.bg }} data-testid={testid}>
      <I size={13} /> Risco {r.label}
    </span>
  );
}

function Trend({ trend }) {
  const t = Number(trend || 0);
  if (t > 0) return <span className="inline-flex items-center gap-1 text-[12px] font-bold text-[#2E7D46]" data-testid="credit-trend"><TrendingUp size={14} /> +{t} pts</span>;
  if (t < 0) return <span className="inline-flex items-center gap-1 text-[12px] font-bold text-[#B4564F]" data-testid="credit-trend"><TrendingDown size={14} /> {t} pts</span>;
  return <span className="inline-flex items-center gap-1 text-[12px] font-bold text-[#5C5A57]" data-testid="credit-trend"><Minus size={14} /> estável</span>;
}

// Medidor do Score Cris 0-1000: trilha com 5 faixas de risco + marcador
function ScoreGauge({ score, label }) {
  const pct = Math.max(0, Math.min(100, (Number(score || 0) / 1000) * 100));
  return (
    <div className="w-full" data-testid="credit-score-gauge">
      <div className="flex items-end gap-3">
        <div className="font-display text-5xl font-bold leading-none" style={{ color: DEEP }} data-testid="credit-score-value">{score}</div>
        <div className="pb-1">
          <div className="text-[10px] tracking-[0.2em] uppercase text-[#8A7788]">Score Cris</div>
          <div className="text-sm font-bold" style={{ color: PURPLE }}>{label}</div>
        </div>
        <div className="ml-auto pb-1 text-[10px] text-[#8A7788]">de 1000</div>
      </div>
      <div className="relative mt-3 h-3 w-full rounded-full overflow-hidden"
        style={{ background: "linear-gradient(90deg,#B4564F 0%,#C2560A 25%,#C9A227 50%,#3F6B45 75%,#2E7D46 100%)" }}>
        <div className="absolute top-[-3px] h-[18px] w-[3px] rounded bg-[#2B1E4A] shadow" style={{ left: `calc(${pct}% - 1.5px)` }} />
      </div>
      <div className="mt-1 flex justify-between text-[9px] text-[#8A7788]"><span>0</span><span>500</span><span>1000</span></div>
    </div>
  );
}

function UnitChip({ label, score, riskLabel, insufficient, testid }) {
  return (
    <div className="rounded-xl border border-[#EBE5DF] bg-white px-3 py-2" data-testid={testid}>
      <div className="text-[9px] tracking-widest uppercase text-[#8A7788]">{label}</div>
      {insufficient ? (
        <div className="text-[12px] font-semibold text-[#8A7788]">Sem histórico</div>
      ) : (
        <div className="flex items-baseline gap-1"><span className="font-display text-xl font-bold" style={{ color: DEEP }}>{score}</span>
          <span className="text-[10px] text-[#8A7788]">{riskLabel}</span></div>
      )}
    </div>
  );
}

function Metric({ label, value, accent, testid }) {
  return (
    <div className="rounded-xl bg-[#FBFAFE] px-3 py-2" data-testid={testid}>
      <div className="text-[9px] tracking-widest uppercase text-[#8A7788]">{label}</div>
      <div className="font-display text-lg font-bold" style={accent ? { color: accent } : { color: DEEP }}>{value}</div>
    </div>
  );
}

export default function CreditCard({ clientId, onLimitChanged }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [a, setA] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState(null);        // {mode, newLimit, reason}
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState(null);
  const [expl, setExpl] = useState(null);
  const [explBusy, setExplBusy] = useState(false);

  const loadExpl = useCallback(async (force = false) => {
    setExplBusy(true);
    try {
      const { data } = await api.get(`/credit/clients/${clientId}/explanation${force ? "?force=true" : ""}`);
      setExpl(data.explanation);
    } catch { setExpl(null); }
    finally { setExplBusy(false); }
  }, [clientId]);
  useEffect(() => { loadExpl(); }, [loadExpl]);

  const load = useCallback(async () => {
    setErr("");
    try {
      const { data } = await api.get(`/credit/clients/${clientId}/analysis`);
      setA(data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Não foi possível carregar a análise de crédito.");
    }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const reprocess = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/credit/clients/${clientId}/analyze?force=true`); setA(data); toast.success("Análise reprocessada"); await loadExpl(true); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro ao reprocessar"); }
    finally { setBusy(false); }
  };

  const openHistory = async () => {
    setShowHistory(true);
    try { const { data } = await api.get(`/credit/clients/${clientId}/history`); setHistory(data); }
    catch { setHistory({ limit_history: [], decisions: [], score_history: [] }); }
  };

  const confirmDecision = async () => {
    const m = modal;
    const body = { decision: m.mode, reason: m.reason || "" };
    if (m.mode === "MODIFIED" || m.mode === "REDUCED") body.new_limit = Number(m.newLimit);
    // Justificativa obrigatoria quando o admin contraria materialmente a recomendacao
    const material = (m.mode === "REDUCED" || m.mode === "DENIED") ||
      (m.mode === "MODIFIED" && Math.abs(Number(m.newLimit) - Number(a.recommended_limit)) > 0.01);
    if (material && !(m.reason || "").trim()) { toast.error("Justificativa obrigatória para esta decisão"); return; }
    if ((m.mode === "MODIFIED" || m.mode === "REDUCED") && (isNaN(Number(m.newLimit)) || Number(m.newLimit) < 0)) {
      toast.error("Informe um limite válido"); return;
    }
    if (m.mode === "REDUCED" && Number(m.newLimit) >= Number(a.current_limit)) { toast.error("Para reduzir, informe um valor menor que o limite atual"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/credit/clients/${clientId}/decision`, body);
      toast.success(data.applied ? `Limite atualizado para ${brl(data.new_limit)}` : "Decisão registrada (sem alteração de limite)");
      setModal(null);
      await load();
      onLimitChanged && onLimitChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao registrar decisão"); }
    finally { setBusy(false); }
  };

  if (err) {
    return (
      <section className="border-2 border-[#E0D6F0] bg-white p-5 mb-6 rounded-2xl" data-testid="credit-card-error">
        <div className="flex items-center gap-2 text-sm font-bold" style={{ color: DEEP }}><Gauge size={18} /> Crédito Inteligente</div>
        <p className="mt-2 text-sm text-[#8B5E5E]">{err}</p>
        <button onClick={load} className="mt-3 rounded-lg px-3 py-1.5 text-xs font-bold text-white" style={{ background: PURPLE }} data-testid="credit-retry-btn">Tentar novamente</button>
      </section>
    );
  }
  if (!a) return <section className="border border-[#EBE5DF] bg-white p-5 mb-6 rounded-2xl text-sm text-[#8A7788]" data-testid="credit-card-loading">Carregando Crédito Inteligente...</section>;

  const positives = (a.factors || []).filter((f) => f.kind === "positive");
  const attention = (a.factors || []).filter((f) => f.kind === "attention");
  const reasonText = positives.length
    ? positives.slice(0, 3).map((f) => f.detail).join("; ") + "."
    : (attention[0]?.detail || "Análise baseada no histórico financeiro do cliente.");
  const cat = CATEGORY[a.category] || CATEGORY.MAINTAIN;
  const us = a.unit_scores || {};

  return (
    <section className="mb-6 rounded-2xl border-2 p-5 shadow-sm" style={{ borderColor: "#E7DBF7", background: "linear-gradient(180deg,#FBF9FF,#FFFFFF)" }} data-testid="credit-card">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="grid h-8 w-8 place-items-center rounded-xl text-white" style={{ background: `linear-gradient(135deg,${PURPLE},${DEEP})` }}><Sparkles size={16} /></span>
          <div>
            <div className="font-display text-lg font-bold" style={{ color: DEEP }}>Crédito Inteligente</div>
            <div className="text-[10px] text-[#8A7788]">Motor {a.engine_version} · política v{a.policy_version} · análise {formatDateTime(a.created_at)}</div>
          </div>
        </div>
        <RiskBadge risk={a.global_risk} testid="credit-global-risk" />
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <div>
          <ScoreGauge score={a.global_score} label={a.global_label} />
          <div className="mt-2 flex items-center gap-3"><Trend trend={a.trend} />
            {a.insufficient_history && <span className="rounded-full bg-[#FBF6E7] px-2 py-0.5 text-[10px] font-bold text-[#B7891B]">Histórico insuficiente</span>}
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <UnitChip label="Cris Lingerie" score={a.cris_score} riskLabel={(RISK[a.cris_risk] || {}).label} insufficient={us.CRIS_LINGERIE?.insufficient_history} testid="credit-score-cris" />
            <UnitChip label="Eletros" score={a.eletros_score} riskLabel={(RISK[a.eletros_risk] || {}).label} insufficient={us.ELETROS?.insufficient_history} testid="credit-score-eletros" />
          </div>
        </div>

        <div>
          <div className="grid grid-cols-2 gap-2">
            <Metric label="Limite atual" value={brl(a.current_limit)} testid="credit-current-limit" />
            <Metric label="Limite recomendado" value={brl(a.recommended_limit)} accent={PURPLE} testid="credit-recommended-limit" />
            <Metric label="Disponível" value={brl(a.available_limit)} testid="credit-available-limit" />
            <Metric label="Utilização" value={`${Math.round((a.utilization_rate || 0) * 100)}%`} testid="credit-utilization" />
          </div>
          {/* Antes/depois */}
          <div className="mt-3 rounded-xl border border-[#E7DBF7] bg-white p-3" data-testid="credit-before-after">
            <div className="flex items-center justify-center gap-3 text-center">
              <div><div className="text-[9px] uppercase tracking-widest text-[#8A7788]">Atual</div><div className="font-display text-xl font-bold" style={{ color: DEEP }}>{brl(a.current_limit)}</div></div>
              <ArrowRight size={20} color={PURPLE} />
              <div><div className="text-[9px] uppercase tracking-widest text-[#8A7788]">Recomendado</div><div className="font-display text-xl font-bold" style={{ color: PURPLE }}>{brl(a.recommended_limit)}</div></div>
              {Number(a.increase) !== 0 && (
                <div className="ml-1 rounded-lg px-2 py-1 text-[11px] font-bold" style={{ color: cat.c, background: cat.bg }}>
                  {a.increase > 0 ? "+" : ""}{brl(a.increase)}{a.increase_pct != null ? ` (${Math.round(a.increase_pct * 100)}%)` : ""}
                </div>
              )}
            </div>
            <div className="mt-1 text-center"><span className="rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ color: cat.c, background: cat.bg }}>{cat.label}</span></div>
            <p className="mt-2 text-[12px] leading-relaxed text-[#5C5A57]" data-testid="credit-reason">{reasonText}</p>
          </div>
        </div>
      </div>

      {/* Explicação inteligente (CREDIT-4) — IA explica os números; nunca calcula/decide */}
      <AIExplanation expl={expl} busy={explBusy} onRefresh={() => loadExpl(true)} />

      {/* Simulacao automatica (DRY-RUN) */}
      {a.would_decision && (
        <div className="mt-3 rounded-xl border border-[#E7DBF7] bg-[#F7F3FE] p-3" data-testid="credit-simulation">
          <div className="mb-1 flex items-center gap-2">
            <span className="rounded-full bg-[#EDE3FB] px-2 py-0.5 text-[10px] font-bold text-[#5B2CA0]">Dry-run</span>
            <span className="text-[10px] uppercase tracking-widest text-[#8A7788]">Simulação automática</span>
          </div>
          <p className="text-[12px] text-[#4A3B63]" data-testid="credit-would-text">{
            a.would_decision === "WOULD_APPROVE" ? `Pelas regras atuais, este cliente SERIA elegível para aumento automático de ${brl(a.current_limit)} para ${brl(a.recommended_limit)}.`
            : a.would_decision === "WOULD_REVIEW" ? "Pelas regras atuais, iria para análise manual (não elegível ao automático)."
            : a.would_decision === "WOULD_REDUCE" ? "Sinais de possível redução — requer análise manual (redução automática desativada)."
            : a.would_decision === "BLOCKED" ? "Aumento automático bloqueado pelas regras atuais."
            : "Sem alteração sugerida no momento."
          }{a.dynamic_limit_eligible ? " Elegível a limite dinâmico." : ""}</p>
          {isAdmin && a.would_decision === "WOULD_APPROVE" && (
            <button onClick={() => setModal({ mode: "APPROVED", newLimit: a.recommended_limit, reason: "" })}
              className="mt-2 rounded-lg px-3 py-1.5 text-[12px] font-bold text-white" style={{ background: PURPLE }} data-testid="credit-review-approve-btn">Revisar e aprovar</button>
          )}
        </div>
      )}

      {/* Bloqueios */}
      {(a.blocked_reasons || []).length > 0 && (
        <div className="mt-3 rounded-lg bg-[#FCEEE2] p-2 text-[12px] text-[#8A4B12]" data-testid="credit-blocked">
          {a.blocked_reasons.map((b) => <div key={b} className="flex items-center gap-1"><ShieldAlert size={13} /> {BLOCKED[b] || b}</div>)}
        </div>
      )}

      {/* Listas de fatores */}
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="rounded-xl border border-[#DCEFE0] bg-[#F5FBF6] p-3" data-testid="credit-positives">
          <div className="mb-1 text-[10px] font-bold uppercase tracking-widest text-[#2E7D46]">Pontos positivos</div>
          {positives.length ? positives.map((f, i) => (
            <div key={i} className="flex items-start gap-1 text-[12px] text-[#2f4a34]"><CheckCircle2 size={13} className="mt-0.5" /> {f.detail}</div>
          )) : <div className="text-[12px] text-[#8A7788]">—</div>}
        </div>
        <div className="rounded-xl border border-[#F1DBC9] bg-[#FDF6EF] p-3" data-testid="credit-attention">
          <div className="mb-1 text-[10px] font-bold uppercase tracking-widest text-[#C2560A]">Pontos de atenção</div>
          {attention.length ? attention.map((f, i) => (
            <div key={i} className="flex items-start gap-1 text-[12px] text-[#7a4a1e]"><AlertTriangle size={13} className="mt-0.5" /> {f.detail}</div>
          )) : <div className="text-[12px] text-[#8A7788]">Nenhum ponto de atenção relevante.</div>}
        </div>
      </div>

      {/* Acoes */}
      <div className="mt-4 flex flex-wrap gap-2">
        {isAdmin && (
          <>
            <button onClick={() => setModal({ mode: "APPROVED", newLimit: a.recommended_limit, reason: "" })} disabled={busy}
              className="rounded-lg px-3 py-1.5 text-[12px] font-bold text-white" style={{ background: PURPLE }} data-testid="credit-approve-btn">Aprovar recomendação</button>
            <button onClick={() => setModal({ mode: "MODIFIED", newLimit: a.recommended_limit, reason: "" })} disabled={busy}
              className="rounded-lg border border-[#5B2CA0] px-3 py-1.5 text-[12px] font-bold text-[#5B2CA0]" data-testid="credit-modify-btn">Definir outro limite</button>
            <button onClick={() => setModal({ mode: "MAINTAINED", newLimit: a.current_limit, reason: "" })} disabled={busy}
              className="rounded-lg border border-[#D6CFC7] px-3 py-1.5 text-[12px] font-bold text-[#5C5A57]" data-testid="credit-maintain-btn">Manter atual</button>
            <button onClick={() => setModal({ mode: "REDUCED", newLimit: Math.max(0, a.current_limit - 50), reason: "" })} disabled={busy}
              className="rounded-lg border border-[#B4564F] px-3 py-1.5 text-[12px] font-bold text-[#B4564F]" data-testid="credit-reduce-btn">Reduzir limite</button>
            <button onClick={() => setModal({ mode: "DENIED", newLimit: a.current_limit, reason: "" })} disabled={busy}
              className="rounded-lg border border-[#B4564F] px-3 py-1.5 text-[12px] font-bold text-[#B4564F]" data-testid="credit-deny-btn"><span className="inline-flex items-center gap-1"><XCircle size={13} /> Negar</span></button>
          </>
        )}
        <button onClick={reprocess} disabled={busy} className="rounded-lg border border-[#D6CFC7] px-3 py-1.5 text-[12px] font-bold text-[#5C5A57]" data-testid="credit-reprocess-btn">
          <span className="inline-flex items-center gap-1"><RefreshCw size={13} /> Reprocessar</span></button>
        <button onClick={openHistory} className="rounded-lg border border-[#D6CFC7] px-3 py-1.5 text-[12px] font-bold text-[#5C5A57]" data-testid="credit-history-btn">
          <span className="inline-flex items-center gap-1"><History size={13} /> Ver histórico</span></button>
      </div>

      {modal && (
        <DecisionModal a={a} modal={modal} setModal={setModal} onConfirm={confirmDecision} busy={busy} />
      )}
      {showHistory && (
        <HistoryModal history={history} onClose={() => setShowHistory(false)} />
      )}
    </section>
  );
}

function AIExplanation({ expl, busy, onRefresh }) {
  if (busy && !expl) {
    return (
      <div className="mt-3 rounded-xl border border-[#E7DBF7] bg-white p-3 text-[12px] text-[#8A7788]" data-testid="credit-ai-loading">
        Gerando explicação inteligente...
      </div>
    );
  }
  if (!expl) return null;
  const isAI = expl.source === "ai";
  return (
    <div className="mt-3 rounded-xl border border-[#E7DBF7] bg-white p-3" data-testid="credit-ai-explanation">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="grid h-6 w-6 place-items-center rounded-lg text-white" style={{ background: `linear-gradient(135deg,${PURPLE},${DEEP})` }}><Bot size={13} /></span>
          <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: DEEP }}>Explicação inteligente</span>
          {isAI ? (
            <span className="rounded-full bg-[#EDE3FB] px-2 py-0.5 text-[9px] font-bold text-[#5B2CA0]" data-testid="credit-ai-badge">IA · {expl.model || expl.provider}</span>
          ) : (
            <span className="rounded-full bg-[#F2EFEA] px-2 py-0.5 text-[9px] font-bold text-[#5C5A57]" data-testid="credit-ai-badge">Explicação automática</span>
          )}
        </div>
        <button onClick={onRefresh} disabled={busy} className="grid h-7 w-7 place-items-center rounded-lg border border-[#E0D6F0] text-[#5B2CA0] disabled:opacity-50" data-testid="credit-ai-refresh-btn" title="Gerar novamente">
          <RefreshCw size={13} className={busy ? "animate-spin" : ""} />
        </button>
      </div>
      {expl.headline && <div className="text-[13px] font-bold" style={{ color: DEEP }} data-testid="credit-ai-headline">{expl.headline}</div>}
      <p className="mt-1 text-[12px] leading-relaxed text-[#4A3B63]" data-testid="credit-ai-summary">{expl.summary}</p>
      {(expl.positives?.length > 0 || expl.attention?.length > 0) && (
        <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
          {expl.positives?.length > 0 && (
            <ul className="space-y-0.5" data-testid="credit-ai-positives">
              {expl.positives.map((p, i) => <li key={i} className="flex items-start gap-1 text-[11px] text-[#2f4a34]"><CheckCircle2 size={12} className="mt-0.5 shrink-0" /> {p}</li>)}
            </ul>
          )}
          {expl.attention?.length > 0 && (
            <ul className="space-y-0.5" data-testid="credit-ai-attention">
              {expl.attention.map((p, i) => <li key={i} className="flex items-start gap-1 text-[11px] text-[#7a4a1e]"><AlertTriangle size={12} className="mt-0.5 shrink-0" /> {p}</li>)}
            </ul>
          )}
        </div>
      )}
      {expl.recommendation_text && (
        <div className="mt-2 rounded-lg bg-[#F7F3FE] px-2.5 py-1.5 text-[11px] font-semibold text-[#4A3B63]" data-testid="credit-ai-recommendation">{expl.recommendation_text}</div>
      )}
      <p className="mt-1 text-[9px] text-[#B0A6B8]">A explicação apenas descreve os números do motor. Nenhum limite é decidido pela IA.</p>
    </div>
  );
}

const DECISION_LABEL = {
  APPROVED: "Aprovar recomendação", MODIFIED: "Definir outro limite",
  MAINTAINED: "Manter limite atual", REDUCED: "Reduzir limite", DENIED: "Negar aumento",
};

function ruleOf(a) {
  if ((a.blocked_reasons || []).length) return "Bloqueado: " + a.blocked_reasons.join(", ");
  if (a.would_decision === "WOULD_APPROVE") return "Score, histórico de compras quitadas e teto de crescimento atendidos";
  if (a.would_decision === "WOULD_REVIEW") return "Aumento sugerido, porém fora do critério de aprovação automática";
  return "Sem aumento recomendado no momento";
}

function DecisionModal({ a, modal, setModal, onConfirm, busy }) {
  const editable = modal.mode === "MODIFIED" || modal.mode === "REDUCED";
  const newLimit = editable ? Number(modal.newLimit || 0) : (modal.mode === "APPROVED" ? a.recommended_limit : a.current_limit);
  const deltaVsCurrent = Number(newLimit) - Number(a.current_limit);
  const deltaVsRec = Number(newLimit) - Number(a.recommended_limit);
  const pctVsCurrent = a.current_limit > 0 ? Math.round((deltaVsCurrent / a.current_limit) * 100) : null;
  const material = (modal.mode === "REDUCED" || modal.mode === "DENIED") || (modal.mode === "MODIFIED" && Math.abs(deltaVsRec) > 0.01);
  return (
    <div className="fixed inset-0 z-[80] grid place-items-center bg-black/50 p-4" onClick={() => setModal(null)}>
      <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-pop" onClick={(e) => e.stopPropagation()} data-testid="credit-decision-modal">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold" style={{ color: DEEP }}>{DECISION_LABEL[modal.mode]}</h3>
          <button onClick={() => setModal(null)} className="grid h-8 w-8 place-items-center rounded-lg hover:bg-muted"><X size={18} /></button>
        </div>
        <div className="space-y-1 rounded-xl bg-[#FBFAFE] p-3 text-[13px]">
          <div className="flex justify-between"><span className="text-[#8A7788]">Score Cris / Risco</span><span className="font-bold" style={{ color: DEEP }}>{a.global_score} · {a.global_label}</span></div>
          <div className="flex justify-between"><span className="text-[#8A7788]">Recomendação do motor</span><span className="font-bold" data-testid="credit-decision-would">{a.would_decision}</span></div>
          <div className="flex justify-between"><span className="text-[#8A7788]">Limite atual</span><span className="font-bold">{brl(a.current_limit)}</span></div>
          <div className="flex justify-between"><span className="text-[#8A7788]">Limite recomendado</span><span className="font-bold" style={{ color: PURPLE }}>{brl(a.recommended_limit)}</span></div>
          <div className="pt-1 text-[11px] text-[#8A7788]" data-testid="credit-decision-rule">Regra: {ruleOf(a)}</div>
        </div>
        {editable && (
          <div className="mt-3">
            <label className="block text-[10px] uppercase tracking-widest text-[#8A7788] mb-1">Novo limite (R$)</label>
            <input type="number" min={0} step="50" value={modal.newLimit}
              onChange={(e) => setModal({ ...modal, newLimit: e.target.value })}
              className="w-full rounded-lg border border-[#E0D6F0] p-2 text-sm" data-testid="credit-decision-input" />
          </div>
        )}
        <div className="mt-3 space-y-1 rounded-xl border border-[#E7DBF7] p-3 text-[13px]" data-testid="credit-decision-summary">
          <div className="flex justify-between"><span className="text-[#8A7788]">Novo limite</span><span className="font-bold" style={{ color: DEEP }}>{brl(newLimit)}</span></div>
          <div className="flex justify-between"><span className="text-[#8A7788]">Diferença vs atual</span><span className={`font-bold ${deltaVsCurrent >= 0 ? "text-[#2E7D46]" : "text-[#B4564F]"}`}>{deltaVsCurrent >= 0 ? "+" : ""}{brl(deltaVsCurrent)}{pctVsCurrent != null ? ` (${pctVsCurrent}%)` : ""}</span></div>
          <div className="flex justify-between"><span className="text-[#8A7788]">Diferença vs recomendação</span><span className="font-bold">{deltaVsRec >= 0 ? "+" : ""}{brl(deltaVsRec)}</span></div>
        </div>
        <div className="mt-3">
          <label className="block text-[10px] uppercase tracking-widest text-[#8A7788] mb-1">
            Justificativa {material && <span className="text-[#B4564F]">*obrigatória</span>}
          </label>
          <textarea rows={2} value={modal.reason} onChange={(e) => setModal({ ...modal, reason: e.target.value })}
            placeholder={material ? "Explique o motivo por contrariar a recomendação" : "Opcional"}
            className="w-full rounded-lg border border-[#E0D6F0] p-2 text-sm" data-testid="credit-decision-reason" />
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={() => setModal(null)} className="rounded-lg border border-[#D6CFC7] px-4 py-2 text-sm font-bold text-[#5C5A57]">Cancelar</button>
          <button onClick={onConfirm} disabled={busy} className="rounded-lg px-4 py-2 text-sm font-bold text-white" style={{ background: PURPLE }} data-testid="credit-decision-confirm">
            {busy ? "..." : "Confirmar"}</button>
        </div>
      </div>
    </div>
  );
}

const SOURCE_LABEL = { MANUAL_ADMIN: "Manual (admin)", AI_AUTO: "Automático", RULE_ENGINE: "Regras" };

function HistoryModal({ history, onClose }) {
  return (
    <div className="fixed inset-0 z-[80] grid place-items-center bg-black/50 p-4" onClick={onClose}>
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-5 shadow-pop" onClick={(e) => e.stopPropagation()} data-testid="credit-history-modal">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold" style={{ color: DEEP }}>Histórico do crédito</h3>
          <button onClick={onClose} className="grid h-8 w-8 place-items-center rounded-lg hover:bg-muted"><X size={18} /></button>
        </div>
        {!history ? <div className="py-6 text-center text-sm text-[#8A7788]">Carregando...</div> : (
          (history.limit_history || []).length === 0 && (history.decisions || []).length === 0 ? (
            <div className="py-6 text-center text-sm text-[#8A7788]" data-testid="credit-history-empty">Nenhuma decisão registrada ainda.</div>
          ) : (
            <div className="relative space-y-3 pl-4">
              {(history.limit_history || []).map((h) => (
                <div key={h.id} className="relative rounded-xl border border-[#EBE5DF] p-3" data-testid={`credit-history-item-${h.id}`}>
                  <div className="absolute -left-[9px] top-4 h-2.5 w-2.5 rounded-full" style={{ background: PURPLE }} />
                  <div className="flex items-center justify-between">
                    <div className="text-[13px] font-bold" style={{ color: DEEP }}>{brl(h.old_limit)} → {brl(h.new_limit)}</div>
                    <span className="rounded-full bg-[#F1ECF7] px-2 py-0.5 text-[10px] font-bold text-[#5B2CA0]">{SOURCE_LABEL[h.source] || h.source}</span>
                  </div>
                  <div className="mt-1 text-[11px] text-[#8A7788]">{formatDateTime(h.created_at)} · Score {h.score ?? "—"}{h.reason ? ` · ${h.reason}` : ""}</div>
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
}
